import type { HistoryItem } from './api'
import { Approval, TOOL_ACCESS, titleCase, type ChatMessage, type ToolCall } from './types'

function parse<T>(json: string | undefined, fallback: T): T {
  try {
    return json ? (JSON.parse(json) as T) : fallback
  } catch {
    return fallback
  }
}

/**
 * Stored chat rows → what the chat shows. Tool calls attach to the assistant
 * turn before them; their outcome comes from the matching function_call_output
 * row, so a finished call doesn't reappear as running after a reload.
 */
export function fromHistory(items: HistoryItem[]): ChatMessage[] {
  const out: ChatMessage[] = []
  const calls = new Map<string, ToolCall>()
  const last = () => out.at(-1)

  for (const item of items) {
    const p = item.payload ?? {}
    if (p.role === 'system') continue

    if (p.type === 'function_call' && p.call_id) {
      const name = p.name ?? 'unknown'
      const call: ToolCall = {
        id: p.call_id,
        name,
        displayName: titleCase(name),
        access: TOOL_ACCESS[name] ?? 'write',
        args: parse(p.arguments, {}),
        status: item.call_approval === Approval.Pending ? 'pending' : item.call_approval === Approval.Denied ? 'denied' : 'completed',
      }
      calls.set(call.id, call)
      const prev = last()
      if (prev?.role === 'assistant') prev.tools.push(call)
      else out.push({ id: `h${item.id}`, role: 'assistant', text: '', tools: [call], at: item.created_at })
      continue
    }

    if (p.type === 'function_call_output' && p.call_id) {
      const call = calls.get(p.call_id)
      const o = parse<{ status?: string; result?: Record<string, unknown> | null; error?: string | null }>(p.output, {})
      if (call) {
        call.status = o.status === 'failed' ? 'failed' : o.status === 'denied' ? 'denied' : 'completed'
        call.result = o.result
        call.error = o.error
      }
      continue
    }

    const text = p.content?.trim()
    if (!text) continue
    out.push({ id: `h${item.id}`, role: p.role === 'user' ? 'user' : 'assistant', text: p.content!, tools: [], at: item.created_at })
  }
  return out
}
